import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Typography, Paper, Divider, Button, Modal } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import CustomReadableTable from "../../components/CustomReadableTable"
import { useMutation, api } from "../../api"
import { useCartStore } from "../../stores/cartStore"
import { useEffect, useState } from "react"
import CustomTextField from "../../components/CustomTextField"
import CustomTextFieldReadable from "../../components/CustomTextFieldReadable"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"
import GrabarIcon from "../../assets/iconos/Grabar.ico"
import CustomTooltip from "../../components/ToolTip"
import { useNavigate } from "react-router-dom"
import CustomBackdrop from "../../components/CustomBackdrop"
import PDFButton from "./components/PDFButton"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const EmisionPedidoWeb = () => {
  const navigate = useNavigate()
  const cart = useCartStore((state) => state.cart)
  const clearUseCartStoreStorage = useCartStore((state) => state.clearUseCartStoreStorage)
  const [formData, setFormData] = useState({
    pednumped: "",
    nickname: "",
    comentario: "",
  })
  const handleSetFormData = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }))
  const [expandedCriterioBusqueda, setExpandedCriterioBusqueda] = useState(true)
  const handleToggleCriterioBusqueda = () => {
    setExpandedCriterioBusqueda((prev) => !prev)
  }

  const [productosInfo, setProductosInfo] = useState([])
  const [calculosTotales, setCalculosTotales] = useState({})

  const [openModalPdf, setOpenModalPdf] = useState(false)
  const [pdfData, setPdfData] = useState(null)
  const [isLoadingPdfData, setIsLoadingPdfData] = useState(false)

  const handleCloseModalPdf = () => {
    setOpenModalPdf(false)
    clearUseCartStoreStorage()
    navigate(-1) // Navega después de cerrar el PDF
  }

  const {
    mutate: getInfoProducts,
    isLoading: isLoadingInfoProducts,
    isRefetching: isRefetchInfoProducts,
    error: errorInfoProducts,
  } = useMutation({
    queryKey: ["isLoadingInfoProducts"],
    fn: async (data) => {
      const response = await api.post("/EmisionPedidoWeb/getInfoProducts", data)

      setProductosInfo(response.data?.data?.productosInfo || [])
      setCalculosTotales(response.data?.data?.caculosTotales || {})
      handleSetFormData("pednumped", response.data?.data?.pedidoCodigoGenerated)
      return response.data
    },
    showError: "modal",
    showSuccess: "silent",
  })

  // Mutation para obtener datos completos del pedido
  const {
    mutateAsync: getPedidoCompleto,
    isLoading: isLoadingPedidoCompleto,
    error: errorPedidoCompleto,
  } = useMutation({
    queryKey: ["getPedidoCompleto"],
    fn: async (pednumped) => {
      const response = await api.post("/EmisionPedidoWeb/getPedidoWebById", { pednumped })
      return response
    },
    showError: "modal",
    showSuccess: "silent",
  })

  // Función para obtener datos del PDF
  const obtenerDatosParaPDF = async (pedidoCodigo) => {
    try {
      setIsLoadingPdfData(true)

      const pedidoCompleto = await getPedidoCompleto(pedidoCodigo)

      // Preparar datos para el PDF
      setPdfData({
        cabecera: pedidoCompleto.data?.cabecera || {},
        detalle: pedidoCompleto.data?.detalle || [],
      })

      clearUseCartStoreStorage()
      setOpenModalPdf(true)
    } catch (error) {
      console.error("Error al obtener datos para PDF:", error)
      alert("Pedido guardado pero hubo error al generar el PDF")
    } finally {
      setIsLoadingPdfData(false)
    }
  }

  // Mutation para guardar pedido con onSuccess
  const {
    mutate: savePedidoWeb,
    isLoading: isLoadingSavePedidoWeb,
    isRefetching: isRefetchSavePedidoWeb,
    error: errorSavePedidoWeb,
  } = useMutation({
    queryKey: ["isLoadingSavePedidoWeb"],
    fn: async (data) => {
      const response = await api.post("/EmisionPedidoWeb/savePedidoWeb", data)
      return response
    },
    showError: "modal",
    showSuccess: "modal",
    onSuccess: (result) => {
      console.log("aaaaaaa", result)
      const { pedidoCodigo: nuevoPedidoCodigo } = result

      if (!nuevoPedidoCodigo) {
        alert("Pedido guardado pero no se obtuvo el código. Contacte soporte.")
        clearUseCartStoreStorage()
        navigate(-1)
        return
      }

      // Llamar a obtener datos completos para PDF
      obtenerDatosParaPDF(nuevoPedidoCodigo)
    },
  })

  useEffect(() => {
    if (!cart || cart.length === 0) return

    getInfoProducts({
      cart,
    })
  }, [])

  // Configurar columnas para la tabla de productos
  const productColumns = [
    {
      accessorKey: "codigo",
      header: "Código",
      size: 100,
      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
    },
    {
      accessorKey: "descripcion",
      header: "Descripción",
      size: 200,
      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
    },
    {
      accessorKey: "pedcantidad",
      header: "Cantidad",
      size: 120,
      Cell: ({ cell }) => <span>{cell.getValue().toFixed(2)}</span>,
    },
    {
      accessorKey: "pedPrecioUnitario",
      header: "Precio Unitario",
      size: 200,
      Cell: ({ cell }) => <span>${cell.getValue().toFixed(2)}</span>,
    },
    {
      accessorKey: "pedDescuentoTotal",
      header: "Descuento",
      size: 150,
      Cell: ({ cell }) => <span>${cell.getValue().toFixed(2)}</span>,
    },
    {
      accessorKey: "pedIvaPorcentaje",
      header: "IVA %",
      size: 150,
      Cell: ({ cell }) => <span>{cell.getValue().toFixed(0)}%</span>,
    },
    {
      accessorKey: "pedPrecioTotalSinAjustes",
      header: "Subtotal",
      size: 150,
      Cell: ({ cell }) => <span>${cell.getValue().toFixed(2)}</span>,
    },
    {
      accessorKey: "pedIvaSobrePrecioTotalSinAjustesConDescuento",
      header: "IVA Total",
      size: 200,
      Cell: ({ cell }) => <span>${cell.getValue().toFixed(2)}</span>,
    },
    {
      accessorKey: "pedPrecioTotalConAjustes",
      header: "Total",
      size: 200,
      Cell: ({ cell }) => (
        <span>
          <b>${cell.getValue().toFixed(2)}</b>
        </span>
      ),
    },
  ]

  const handleCreatePedidoWeb = () => {
    if (!formData.nickname || formData.nickname === "") {
      return alert("Digite un nickname, es obligatorio")
    }

    // Solo llama mutate, el onSuccess se encargará del resto
    savePedidoWeb({
      pednumped: formData.pednumped,
      nickname: formData.nickname,
      comentario: formData.comentario,
      productosInfo,
      calculosTotales,
    })
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Emisión de Pedido Web</b>
        </div>

        <CustomBackdrop isLoading={isLoadingSavePedidoWeb || isRefetchSavePedidoWeb || isLoadingPdfData} />

        <Box className={StyledRoot}>
          <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "5px" }}>
              <CustomTooltip title={"Grabar"}>
                <Button onClick={handleCreatePedidoWeb}>
                  <img src={GrabarIcon} alt="Grabar" style={{ width: "40px" }} />
                </Button>
              </CustomTooltip>
            </div>
          </div>
          <CustomFieldsetAccordion
            title="Información Pedido Web"
            expanded={expandedCriterioBusqueda}
            onToggle={handleToggleCriterioBusqueda}
          >
            <CustomTextFieldReadable
              label="Pedido ID"
              value={formData.pednumped}
              sxInput={{
                backgroundColor: "#E0CB79",
              }}
            ></CustomTextFieldReadable>
            <CustomTextField
              label="Nickname"
              value={formData.nickname}
              setValue={(v) => handleSetFormData("nickname", v)}
            ></CustomTextField>
            <CustomTextField
              label="Comentario"
              value={formData.comentario}
              setValue={(v) => handleSetFormData("comentario", v)}
            />
          </CustomFieldsetAccordion>
          <div style={{ marginBottom: "30px" }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: "#1976d2" }}>
              Detalle de Productos
            </Typography>
            <CustomReadableTable
              data={productosInfo}
              isLoading={isLoadingInfoProducts || isRefetchInfoProducts}
              columnsTable={productColumns}
              isError={errorInfoProducts}
              errorMsgFilterSearch="No se pudo encontrar la información de productos"
              refetch={() => {}}
            />
          </div>

          {/* Resumen de Totales como Pedido */}
          <Paper
            elevation={3}
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: "#1976d2" }}>
              Resumen de Pedido
            </Typography>

            <Box sx={{ width: "100%", maxWidth: 500, margin: "0 auto" }}>
              {/* Línea 1: Subtotal Bruto */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">Subtotal Bruto:</Typography>
                <Typography variant="body1">${(calculosTotales.subtotalBrutoTotal || 0).toFixed(2)}</Typography>
              </Box>

              {/* Línea 2: Descuento Total */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">Descuento Total:</Typography>
                <Typography variant="body1" color="error">
                  -${(calculosTotales.descuentoTotalGeneral || 0).toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Línea 3: Subtotal con Descuento */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  Subtotal con Descuento:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  ${(calculosTotales.subtotalBrutoTotalConDescuento || 0).toFixed(2)}
                </Typography>
              </Box>

              {/* Línea 4: IVA Total */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">
                  IVA ({calculosTotales.existeAlgunArticuloConIva === -1 ? "15%" : "0%"}):
                </Typography>
                <Typography variant="body1">
                  ${(calculosTotales.ivaTotalGeneralConDescuento || 0).toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Línea 5: TOTAL NETO (Destacado) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                  border: "1px solid #1976d2",
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="#1976d2">
                  TOTAL NETO:
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="#1976d2">
                  ${(calculosTotales.totalNeto || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Modal para PDF */}
          <Modal
            open={openModalPdf}
            onClose={handleCloseModalPdf}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            disableBackdropClick
            disableEscapeKeyDown
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                Pedido Guardado - ¿Desea ver/descargar el PDF?
              </h2>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button variant="contained" color="error" onClick={handleCloseModalPdf} sx={{ marginRight: "10px" }}>
                  No, salir
                </Button>
              </div>

              {pdfData && <PDFButton proforma={pdfData} handleCloseModalPdf={handleCloseModalPdf} />}

              {errorPedidoCompleto && (
                <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                  Error al cargar datos del pedido: {errorPedidoCompleto.message}
                </Typography>
              )}

              {isLoadingPedidoCompleto && (
                <Typography sx={{ mt: 2, textAlign: "center" }}>Cargando datos del pedido...</Typography>
              )}
            </Box>
          </Modal>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EmisionPedidoWeb
